import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Paper, IconButton, Button, Chip,
  Avatar, Divider, Tooltip, MenuItem, Select, FormControl,
  InputLabel, CircularProgress, Badge, Popover, Checkbox, FormControlLabel,
  Table, TableHead, TableBody, TableRow, TableCell, TextField,
} from '@mui/material';
import TableRowsIcon from '@mui/icons-material/TableRows';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import LinkedInImportDialog from './LinkedInImportDialog';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import CampaignIcon from '@mui/icons-material/Campaign';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import RateReviewIcon from '@mui/icons-material/RateReview';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { DndContext, DragOverlay, rectIntersection, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { useDroppable, useDraggable } from '@dnd-kit/core';
import {
  collection, query, onSnapshot, updateDoc, doc, orderBy,
  serverTimestamp, getDoc, deleteDoc, addDoc,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import LagbasLogo from '../components/LagbasLogo';

// ── Konstanter ──────────────────────────────────────────────────────────────

export const STATUSES = [
  { id: 'idé',        label: 'Idé',        icon: <LightbulbOutlinedIcon fontSize="small" />,   color: '#757575' },
  { id: 'utkast',     label: 'Utkast',     icon: <ArticleOutlinedIcon fontSize="small" />,      color: '#1565C0' },
  { id: 'granskning', label: 'Granskning', icon: <RateReviewIcon fontSize="small" />,           color: '#C62828' },
  { id: 'klar',       label: 'Klar',       icon: <CheckCircleIcon fontSize="small" />,          color: '#2E7D32' },
  { id: 'schemalagd', label: 'Schemalagd', icon: <ScheduleIcon fontSize="small" />,             color: '#E65100' },
  { id: 'publicerad', label: 'Publicerad', icon: <PublishedWithChangesIcon fontSize="small" />, color: '#6A1B9A' },
];

export const REVIEWER_ROLES = ['superadmin', 'admin', 'marknadsansvarig', 'ao_ansvarig'];

export const POST_TYPES = {
  tikab:      { label: 'Tikab',       color: '#FF6B00', bg: '#FFF3E0' },
  btm:        { label: 'BTM',         color: '#1565C0', bg: '#E3F2FD' },
  iv:         { label: 'IV',          color: '#2E7D32', bg: '#E8F5E9' },
  mek:        { label: 'MEK',         color: '#6A1B9A', bg: '#F3E5F5' },
  ambassadör: { label: 'Ambassadör',  color: '#C62828', bg: '#FFEBEE' },
  produkt:    { label: 'Produkt',     color: '#00838F', bg: '#E0F7FA' },
};

const WEEKDAYS = ['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön'];

// ── Hjälpfunktioner ──────────────────────────────────────────────────────────

function fmtDate(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' });
}

function daysSince(ts) {
  if (!ts) return null;
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return Math.floor((Date.now() - d.getTime()) / 86400000);
}

function fmtDays(n) {
  if (n === null) return null;
  if (n === 0) return 'Idag';
  if (n === 1) return 'Igår';
  return `${n} d sedan`;
}

function daysChipColor(n) {
  if (n === null) return { bg: '#f5f5f5', color: '#999' };
  if (n <= 2)  return { bg: '#E8F5E9', color: '#2E7D32' };
  if (n <= 7)  return { bg: '#FFF8E1', color: '#F57F17' };
  if (n <= 21) return { bg: '#FFF3E0', color: '#E65100' };
  return { bg: '#FFEBEE', color: '#C62828' };
}

function getInitials(name = '') {
  return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

// ── DnD-komponenter ─────────────────────────────────────────────────────────

function DroppableColumn({ id, children, isOver }) {
  const { setNodeRef } = useDroppable({ id });
  return (
    <Box
      ref={setNodeRef}
      sx={{
        flex: '1 1 0',
        minWidth: 0,
        bgcolor: isOver ? 'rgba(10,102,194,0.08)' : 'action.hover',
        borderRadius: 2,
        border: isOver ? '2px dashed #0a66c2' : '2px dashed transparent',
        transition: 'border-color 0.15s, background 0.15s',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 200,
      }}
    >
      {children}
    </Box>
  );
}

function DraggableCard({ post, navigate, onReminderClick, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: post.id });
  const t = POST_TYPES[post.type] || POST_TYPES.tikab;
  const tc = post.themeColor || t.color;

  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, zIndex: 999 }
    : undefined;

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      elevation={isDragging ? 6 : 1}
      {...listeners}
      {...attributes}
      onClick={() => navigate(`/marketing/linkedin/${post.id}`)}
      sx={{
        mx: 1, mb: 1, p: 1.5,
        borderRadius: 2,
        opacity: isDragging ? 0.4 : 1,
        border: post.isPlaceholder ? `1px dashed ${tc}` : undefined,
        borderLeft: `4px solid ${tc}`,
        cursor: isDragging ? 'grabbing' : 'grab',
        transition: 'box-shadow 0.15s, opacity 0.15s',
        '&:hover': { boxShadow: 3 },
        bgcolor: post.isPlaceholder ? tc + '08' : 'background.paper',
      }}
    >
      {/* Drag handle (visual only) + edit */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, gap: 0.5 }}>
        <Box sx={{ color: '#bbb', display: 'flex', mr: 0.5 }}>
          <DragIndicatorIcon sx={{ fontSize: '1rem' }} />
        </Box>
        {post.isPlaceholder ? (
          <Chip label="📅 Platshållare" size="small"
            sx={{ bgcolor: tc + '22', color: tc, fontWeight: 700, fontSize: '0.65rem', height: 18, border: `1px dashed ${tc}` }} />
        ) : (
          <Chip label={t.label} size="small"
            sx={{ bgcolor: t.bg, color: t.color, fontWeight: 700, fontSize: '0.65rem', height: 18 }} />
        )}
        {post.themeName && (
          <Chip label={post.themeName} size="small"
            sx={{ bgcolor: tc + '18', color: tc, fontWeight: 600, fontSize: '0.65rem', height: 18, mr: 'auto' }} />
        )}
        {post.themeLabel && !post.themeName && (
          <Chip label={post.themeLabel} size="small"
            sx={{ bgcolor: '#E0F7FA', color: '#00838F', fontWeight: 600, fontSize: '0.65rem', height: 18, mr: 'auto' }} />
        )}
        {!post.themeLabel && !post.themeName && <Box sx={{ mr: 'auto' }} />}
        {post.reminders?.length > 0 && (
          <Tooltip title="Påminnelse satt">
            <NotificationsNoneIcon sx={{ fontSize: '0.9rem', color: '#E65100' }} />
          </Tooltip>
        )}
        <Tooltip title="Redigera inlägg">
          <IconButton
            size="small"
            onPointerDown={e => e.stopPropagation()}
            onClick={e => { e.stopPropagation(); navigate(`/marketing/linkedin/${post.id}`); }}
            sx={{ p: 0.3 }}
          >
            <EditIcon sx={{ fontSize: '0.9rem' }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Radera inlägg">
          <IconButton
            size="small"
            onPointerDown={e => e.stopPropagation()}
            onClick={e => {
              e.stopPropagation();
              if (window.confirm(`Radera "${post.title || 'Namnlöst inlägg'}"?`)) {
                onDelete?.(post.id);
              }
            }}
            sx={{ p: 0.3, color: '#999', '&:hover': { color: '#d32f2f' } }}
          >
            <DeleteOutlineIcon sx={{ fontSize: '0.9rem' }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Title */}
      <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.3, mb: post.isPlaceholder && post.productName ? 0.25 : 0.5 }}>
        {post.title || '(Namnlöst inlägg)'}
      </Typography>
      {post.isPlaceholder && post.productName && (
        <Chip label={post.productName} size="small"
          sx={{ bgcolor: '#E0F7FA', color: '#00838F', fontWeight: 600, fontSize: '0.6rem', height: 16, mb: 0.5 }} />
      )}

      {/* Preview text */}
      {post.content && (
        <Typography sx={{
          fontSize: '0.72rem', color: 'text.secondary', lineHeight: 1.4,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}>
          {post.content}
        </Typography>
      )}

      {post.type === 'produkt' && post.productName && (
        <Typography sx={{ fontSize: '0.7rem', color: '#00838F', fontWeight: 600, mt: 0.5 }}>
          {post.productName} • 3-nivå
        </Typography>
      )}

      {/* Footer */}
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 0.5 }}>
        {post.scheduledDate && (
          <Chip
            label={fmtDate(post.scheduledDate)}
            size="small"
            icon={<ScheduleIcon style={{ fontSize: '0.7rem' }} />}
            sx={{ fontSize: '0.65rem', height: 18, bgcolor: '#f0f4ff', color: '#1565C0' }}
          />
        )}
        {(() => {
          const days = daysSince(post.updatedAt || post.createdAt);
          const label = fmtDays(days);
          const { bg, color } = daysChipColor(days);
          if (!label) return null;
          return (
            <Tooltip title={`Senast uppdaterad: ${days === 0 ? 'idag' : days === 1 ? 'igår' : `${days} dagar sedan`}`}>
              <Chip label={label} size="small"
                sx={{ fontSize: '0.62rem', height: 16, bgcolor: bg, color, fontWeight: 600, cursor: 'default' }} />
            </Tooltip>
          );
        })()}
        {post.assignedToName && (
          <Tooltip title={post.assignedToName}>
            <Avatar sx={{ width: 18, height: 18, fontSize: '0.55rem', bgcolor: '#FF6B00', ml: 'auto' }}>
              {getInitials(post.assignedToName)}
            </Avatar>
          </Tooltip>
        )}
      </Box>

      {/* Statistik – visas bara för publicerade inlägg med data */}
      {post.status === 'publicerad' && post.stats && (
        Number(post.stats.visningar) > 0 || Number(post.stats.reaktioner) > 0
      ) && (() => {
        const v = Number(post.stats.visningar) || 0;
        const r = Number(post.stats.reaktioner) || 0;
        const k = Number(post.stats.kommentarer) || 0;
        const d = Number(post.stats.delningar) || 0;
        const eng = v > 0 ? (((r + k + d) / v) * 100).toFixed(1) : null;
        return (
          <Box sx={{
            mt: 1, pt: 1,
            borderTop: '1px dashed #e0e0e0',
            display: 'flex', gap: 1, flexWrap: 'wrap',
          }}>
            {v > 0 && (
              <Tooltip title="Visningar">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                  <VisibilityOutlinedIcon sx={{ fontSize: '0.75rem', color: '#1565C0' }} />
                  <Typography sx={{ fontSize: '0.68rem', color: '#555', fontWeight: 600 }}>
                    {v.toLocaleString('sv-SE')}
                  </Typography>
                </Box>
              </Tooltip>
            )}
            {r > 0 && (
              <Tooltip title="Reaktioner">
                <Typography sx={{ fontSize: '0.68rem', color: '#C62828' }}>
                  👍 {r}
                </Typography>
              </Tooltip>
            )}
            {k > 0 && (
              <Tooltip title="Kommentarer">
                <Typography sx={{ fontSize: '0.68rem', color: '#2E7D32' }}>
                  💬 {k}
                </Typography>
              </Tooltip>
            )}
            {d > 0 && (
              <Tooltip title="Delningar">
                <Typography sx={{ fontSize: '0.68rem', color: '#E65100' }}>
                  🔁 {d}
                </Typography>
              </Tooltip>
            )}
            {eng && (
              <Tooltip title="Engagemang (reaktioner+kommentarer+delningar / visningar)">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, ml: 'auto' }}>
                  <TrendingUpIcon sx={{ fontSize: '0.75rem', color: '#6A1B9A' }} />
                  <Typography sx={{ fontSize: '0.68rem', color: '#6A1B9A', fontWeight: 700 }}>
                    {eng}%
                  </Typography>
                </Box>
              </Tooltip>
            )}
          </Box>
        );
      })()}
    </Paper>
  );
}

// ── Kalendervy ───────────────────────────────────────────────────────────────

function CalendarView({ posts, navigate }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();

  // Bygg grid: måndag = dag 0
  const firstDay = new Date(year, month, 1);
  let startDow = firstDay.getDay(); // 0=sön
  startDow = startDow === 0 ? 6 : startDow - 1; // konvertera till mån=0
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const postsForDay = (day) => {
    if (!day) return [];
    return posts.filter(p => {
      if (!p.scheduledDate) return false;
      const d = p.scheduledDate.toDate ? p.scheduledDate.toDate() : new Date(p.scheduledDate);
      return d.getFullYear() === year && d.getMonth() === month && d.getDate() === day;
    });
  };

  const today = new Date();
  const isToday = (day) =>
    day && today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;

  const monthLabel = currentMonth.toLocaleDateString('sv-SE', { month: 'long', year: 'numeric' });

  return (
    <Box>
      {/* Month nav */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton onClick={() => setCurrentMonth(new Date(year, month - 1, 1))} size="small">
          <ChevronLeftIcon />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: 700, textTransform: 'capitalize', minWidth: 180, textAlign: 'center' }}>
          {monthLabel}
        </Typography>
        <IconButton onClick={() => setCurrentMonth(new Date(year, month + 1, 1))} size="small">
          <ChevronRightIcon />
        </IconButton>
        <Button
          size="small"
          startIcon={<TodayIcon />}
          onClick={() => setCurrentMonth(new Date())}
          sx={{ ml: 1 }}
        >
          Idag
        </Button>
      </Box>

      {/* Weekday headers */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 0.5, gap: 0.5 }}>
        {WEEKDAYS.map(d => (
          <Typography key={d} sx={{ textAlign: 'center', fontSize: '0.75rem', fontWeight: 700, color: '#666', py: 0.5 }}>
            {d}
          </Typography>
        ))}
      </Box>

      {/* Calendar grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
        {cells.map((day, idx) => {
          const dayPosts = postsForDay(day);
          return (
            <Paper
              key={idx}
              elevation={0}
              sx={{
                minHeight: 90,
                p: 0.75,
                borderRadius: 1.5,
                bgcolor: day ? (isToday(day) ? '#FFF3E0' : '#fafafa') : 'transparent',
                border: isToday(day) ? '2px solid #FF6B00' : '1px solid #e0e0e0',
                opacity: day ? 1 : 0,
              }}
            >
              {day && (
                <>
                  <Typography sx={{
                    fontSize: '0.78rem', fontWeight: isToday(day) ? 800 : 500,
                    color: isToday(day) ? '#FF6B00' : '#1a1a1a', mb: 0.5,
                  }}>
                    {day}
                  </Typography>
                  {dayPosts.slice(0, 3).map(p => {
                    if (p.isPlaceholder) {
                      const tc = p.themeColor || '#9E9E9E';
                      return (
                        <Tooltip key={p.id} title={`📅 ${p.themeName || 'Kampanj'} — ${p.campaignName || ''}`}>
                          <Box onClick={() => navigate(`/marketing/linkedin/${p.id}`)}
                            sx={{ cursor: 'pointer', mb: 0.3 }}>
                            <Chip label={p.themeName || 'Platshållare'} size="small" sx={{
                              bgcolor: tc + '22', color: tc, fontWeight: 700, border: `1px dashed ${tc}`,
                              fontSize: '0.6rem', height: 16, width: '100%', cursor: 'pointer',
                              justifyContent: 'flex-start',
                              '& .MuiChip-label': { px: 0.75, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' },
                            }} />
                          </Box>
                        </Tooltip>
                      );
                    }
                    const t = POST_TYPES[p.type] || POST_TYPES.tikab;
                    const v = Number(p.stats?.visningar) || 0;
                    const r = Number(p.stats?.reaktioner) || 0;
                    const k = Number(p.stats?.kommentarer) || 0;
                    const d = Number(p.stats?.delningar) || 0;
                    const eng = v > 0 ? (((r + k + d) / v) * 100).toFixed(1) : null;
                    const tooltipText = [
                      p.title || t.label,
                      v > 0 ? `👁 ${v.toLocaleString('sv-SE')} visningar` : '',
                      eng ? `📈 ${eng}% engagemang` : '',
                    ].filter(Boolean).join(' • ');
                    return (
                      <Tooltip key={p.id} title={tooltipText}>
                        <Box
                          onClick={() => navigate(`/marketing/linkedin/${p.id}`)}
                          sx={{ cursor: 'pointer', mb: 0.3 }}
                        >
                          <Chip
                            label={p.status === 'publicerad' && eng ? `${t.label} ${eng}%` : t.label}
                            size="small"
                            sx={{
                              bgcolor: t.bg, color: t.color, fontWeight: 700,
                              fontSize: '0.6rem', height: 16, width: '100%',
                              cursor: 'pointer', justifyContent: 'flex-start',
                              '& .MuiChip-label': { px: 0.75, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' },
                            }}
                          />
                        </Box>
                      </Tooltip>
                    );
                  })}
                  {dayPosts.length > 3 && (
                    <Typography sx={{ fontSize: '0.6rem', color: '#888', mt: 0.3 }}>
                      +{dayPosts.length - 3} till
                    </Typography>
                  )}
                </>
              )}
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
}

// ── Gridredigering ───────────────────────────────────────────────────────────

const GRID_COLUMNS = [
  { id: 'title',         label: 'Rubrik',       defaultOn: true  },
  { id: 'type',          label: 'Typ',           defaultOn: true  },
  { id: 'theme',         label: 'Tema',          defaultOn: true  },
  { id: 'status',        label: 'Status',        defaultOn: true  },
  { id: 'scheduledDate', label: 'Schemalagd',    defaultOn: true  },
  { id: 'assignedTo',    label: 'Ansvarig',      defaultOn: true  },
  { id: 'content',       label: 'Innehåll',      defaultOn: false },
  { id: 'description',   label: 'Beskrivning',   defaultOn: false },
];

function GridView({ posts, onUpdate, navigate }) {
  const [statusFilter, setStatusFilter] = useState('');
  const [visibleCols, setVisibleCols]   = useState(
    () => new Set(GRID_COLUMNS.filter(c => c.defaultOn).map(c => c.id))
  );
  const [colAnchor, setColAnchor] = useState(null);
  const [editing, setEditing]     = useState({}); // postId → fieldId

  const filtered = statusFilter
    ? posts.filter(p => p.status === statusFilter)
    : posts;

  function toggleCol(id) {
    setVisibleCols(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function startEdit(postId, field) { setEditing({ postId, field }); }
  function stopEdit() { setEditing({}); }

  function handleFieldSave(post, field, value) {
    if (post[field] === value) { stopEdit(); return; }
    onUpdate(post.id, { [field]: value });
    stopEdit();
  }

  function EditableText({ post, field, multiline }) {
    const [val, setVal] = useState(post[field] || '');
    const isEditing = editing.postId === post.id && editing.field === field;
    if (isEditing) {
      return (
        <TextField
          autoFocus size="small" fullWidth multiline={multiline} rows={multiline ? 3 : 1}
          value={val}
          onChange={e => setVal(e.target.value)}
          onBlur={() => handleFieldSave(post, field, val)}
          onKeyDown={e => { if (e.key === 'Enter' && !multiline) handleFieldSave(post, field, val); if (e.key === 'Escape') stopEdit(); }}
          sx={{ '& .MuiInputBase-input': { fontSize: '0.82rem' } }}
        />
      );
    }
    return (
      <Typography
        variant="body2" onClick={() => startEdit(post.id, field)}
        sx={{
          fontSize: '0.82rem', cursor: 'text', px: 0.5, py: 0.25,
          borderRadius: 1, minWidth: 60, minHeight: 24,
          '&:hover': { bgcolor: 'action.hover' },
          ...(multiline ? {
            display: '-webkit-box', WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical', overflow: 'hidden',
          } : { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 260 }),
        }}
      >
        {post[field] || <span style={{ color: '#bbb' }}>—</span>}
      </Typography>
    );
  }

  function ThemeCell({ post }) {
    const theme = post.themeName || post.themeLabel || '';
    const color = post.themeColor || '#00838F';
    if (!theme) return <Typography variant="caption" color="text.disabled">—</Typography>;
    return (
      <Chip label={theme} size="small" sx={{
        fontSize: '0.68rem', height: 20,
        bgcolor: post.themeName ? color + '22' : '#E0F7FA',
        color: post.themeName ? color : '#00838F',
        fontWeight: 600,
      }} />
    );
  }

  return (
    <Box>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Select size="small" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          displayEmpty sx={{ minWidth: 150 }}>
          <MenuItem value=""><em>Alla statusar</em></MenuItem>
          {STATUSES.map(s => (
            <MenuItem key={s.id} value={s.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ color: s.color }}>{s.icon}</Box> {s.label}
              </Box>
            </MenuItem>
          ))}
        </Select>

        <Typography variant="caption" color="text.secondary">
          {filtered.length} inlägg
        </Typography>

        <Box sx={{ flex: 1 }} />

        <Tooltip title="Välj kolumner">
          <IconButton size="small" onClick={e => setColAnchor(e.currentTarget)}>
            <ViewColumnIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Popover open={Boolean(colAnchor)} anchorEl={colAnchor} onClose={() => setColAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { p: 1.5, borderRadius: 2, minWidth: 160 } }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700}
            sx={{ textTransform: 'uppercase', letterSpacing: 0.6, px: 0.5, display: 'block', mb: 0.5 }}>
            Kolumner
          </Typography>
          {GRID_COLUMNS.map(c => (
            <FormControlLabel key={c.id}
              control={<Checkbox size="small" checked={visibleCols.has(c.id)} onChange={() => toggleCol(c.id)}
                sx={{ '&.Mui-checked': { color: '#FF6B00' } }} />}
              label={<Typography variant="body2">{c.label}</Typography>}
              sx={{ display: 'flex', ml: 0, mr: 0 }} />
          ))}
        </Popover>
      </Box>

      {/* Tabell */}
      <Paper sx={{ overflow: 'hidden' }}>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ '& td, & th': { py: 0.75, px: 1.5 } }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                {visibleCols.has('title')         && <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, minWidth: 200 }}>Rubrik</TableCell>}
                {visibleCols.has('type')          && <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, minWidth: 110 }}>Typ</TableCell>}
                {visibleCols.has('theme')         && <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, minWidth: 130 }}>Tema</TableCell>}
                {visibleCols.has('status')        && <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, minWidth: 130 }}>Status</TableCell>}
                {visibleCols.has('scheduledDate') && <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, minWidth: 120 }}>Schemalagd</TableCell>}
                {visibleCols.has('assignedTo')    && <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, minWidth: 120 }}>Ansvarig</TableCell>}
                {visibleCols.has('content')       && <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, minWidth: 200 }}>Innehåll</TableCell>}
                {visibleCols.has('description')   && <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, minWidth: 200 }}>Beskrivning</TableCell>}
                <TableCell sx={{ width: 40 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={visibleCols.size + 1} sx={{ textAlign: 'center', py: 4, color: 'text.disabled' }}>
                    Inga inlägg
                  </TableCell>
                </TableRow>
              )}
              {filtered.map(post => {
                const t = POST_TYPES[post.type] || POST_TYPES.tikab;
                const s = STATUSES.find(s => s.id === post.status) || STATUSES[0];
                return (
                  <TableRow key={post.id} hover sx={{ '&:last-child td': { border: 0 } }}>
                    {visibleCols.has('title') && (
                      <TableCell><EditableText post={post} field="title" /></TableCell>
                    )}
                    {visibleCols.has('type') && (
                      <TableCell>
                        <Select size="small" value={post.type || 'tikab'}
                          onChange={e => onUpdate(post.id, { type: e.target.value })}
                          sx={{ fontSize: '0.75rem', color: t.color,
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: t.color + '55' },
                            '& .MuiSelect-select': { py: 0.4, px: 1 } }}>
                          {Object.entries(POST_TYPES).map(([k, v]) => (
                            <MenuItem key={k} value={k} sx={{ fontSize: '0.82rem' }}>{v.label}</MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                    )}
                    {visibleCols.has('theme') && (
                      <TableCell><ThemeCell post={post} /></TableCell>
                    )}
                    {visibleCols.has('status') && (
                      <TableCell>
                        <Select size="small" value={post.status || 'idé'}
                          onChange={e => onUpdate(post.id, { status: e.target.value })}
                          sx={{ fontSize: '0.75rem', color: s.color,
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: s.color + '55' },
                            '& .MuiSelect-select': { py: 0.4, px: 1 } }}>
                          {STATUSES.map(st => (
                            <MenuItem key={st.id} value={st.id} sx={{ fontSize: '0.82rem', color: st.color }}>{st.label}</MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                    )}
                    {visibleCols.has('scheduledDate') && (
                      <TableCell>
                        <TextField type="date" size="small"
                          value={post.scheduledDate
                            ? (post.scheduledDate.toDate
                                ? post.scheduledDate.toDate()
                                : new Date(post.scheduledDate)
                              ).toISOString().slice(0, 10)
                            : ''}
                          onChange={e => {
                            const val = e.target.value;
                            onUpdate(post.id, { scheduledDate: val ? new Date(val + 'T12:00:00') : null });
                          }}
                          InputLabelProps={{ shrink: true }}
                          sx={{ '& .MuiInputBase-input': { fontSize: '0.8rem', py: 0.5, px: 1 }, minWidth: 130 }} />
                      </TableCell>
                    )}
                    {visibleCols.has('assignedTo') && (
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>
                          {post.assignedToName || <span style={{ color: '#bbb' }}>—</span>}
                        </Typography>
                      </TableCell>
                    )}
                    {visibleCols.has('content') && (
                      <TableCell><EditableText post={post} field="content" multiline /></TableCell>
                    )}
                    {visibleCols.has('description') && (
                      <TableCell><EditableText post={post} field="description" multiline /></TableCell>
                    )}
                    <TableCell>
                      <Tooltip title="Öppna">
                        <IconButton size="small" onClick={() => navigate(`/marketing/linkedin/${post.id}`)}
                          sx={{ p: 0.3 }}>
                          <EditIcon sx={{ fontSize: '0.9rem' }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </Paper>
    </Box>
  );
}

// ── Huvud-komponent ──────────────────────────────────────────────────────────

export default function LinkedInVerkstad() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('board');
  const [filterType, setFilterType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [userName, setUserName] = useState('');
  const [userProfile, setUserProfile] = useState({});
  const [hiddenColumns, setHiddenColumns] = useState(() => {
    try { return JSON.parse(localStorage.getItem('linkedin_hiddenCols') || '[]'); } catch { return []; }
  });
  const [colAnchor, setColAnchor] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [boardSort, setBoardSort] = useState('updated');

  const isReviewer = REVIEWER_ROLES.includes(userRole)
    || userProfile?.canManageMarketing === true
    || userProfile?.isAdmin === true;

  useEffect(() => {
    if (!currentUser) return;
    getDoc(doc(db, 'users', currentUser.uid)).then(snap => {
      const d = snap.data() || {};
      setUserRole(d.role || '');
      setUserName(`${d.firstName || ''} ${d.lastName || ''} ${d.name || ''}`.trim().split(/\s+/).slice(0,2).join(' '));
      setUserProfile(d);
      if (Array.isArray(d.linkedinHiddenCols)) setHiddenColumns(d.linkedinHiddenCols);
    });
  }, [currentUser]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  // Ladda inlägg
  useEffect(() => {
    const q = query(collection(db, 'linkedinPosts'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, snap => {
      setPosts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, []);

  const filteredPosts = posts.filter(p => {
    if (p.isPlaceholder) return true;
    if (filterType && p.type !== filterType) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const haystack = [p.title, p.content, p.assignedToName, p.productName, p.themeLabel]
        .filter(Boolean).join(' ').toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });

  const postsForStatus = (status) => {
    const ps = filteredPosts.filter(p => p.status === status);
    if (boardSort === 'title')   return [...ps].sort((a, b) => (a.title || '').localeCompare(b.title || '', 'sv'));
    if (boardSort === 'type')    return [...ps].sort((a, b) => (a.type || '').localeCompare(b.type || '', 'sv'));
    if (boardSort === 'theme')   return [...ps].sort((a, b) =>
      (a.themeName || a.themeLabel || '').localeCompare(b.themeName || b.themeLabel || '', 'sv')
    );
    if (boardSort === 'updated') return [...ps].sort((a, b) => {
      const ta = (a.updatedAt || a.createdAt)?.toDate?.() ?? new Date(a.updatedAt || 0);
      const tb = (b.updatedAt || b.createdAt)?.toDate?.() ?? new Date(b.updatedAt || 0);
      return tb - ta;
    });
    return ps;
  };
  const visibleStatuses = STATUSES.filter(s => !hiddenColumns.includes(s.id));

  // Drag & drop
  const handleDragStart = useCallback(({ active }) => setActiveId(active.id), []);
  const handleDragOver = useCallback(({ over }) => setOverId(over?.id ?? null), []);

  const handleDelete = useCallback(async (postId) => {
    setPosts(prev => prev.filter(p => p.id !== postId));
    try {
      await deleteDoc(doc(db, 'linkedinPosts', postId));
    } catch (e) {
      console.error('Kunde inte radera inlägg:', e);
    }
  }, []);

  const handleDragEnd = useCallback(async ({ active, over }) => {
    setActiveId(null);
    setOverId(null);
    if (!over) return;

    const statusIds = STATUSES.map(s => s.id);
    let newStatus = over.id;
    if (!statusIds.includes(newStatus)) {
      // dropped on a card – find that card's column
      const target = posts.find(p => p.id === newStatus);
      if (!target) return;
      newStatus = target.status;
    }

    const postId = active.id;
    const current = posts.find(p => p.id === postId);
    if (!current || current.status === newStatus) return;

    // Bara granskare får flytta från granskning → klar/schemalagd/publicerad
    const reviewerOnly = ['klar', 'schemalagd', 'publicerad'];
    if (current.status === 'granskning' && reviewerOnly.includes(newStatus) && !isReviewer) return;

    setPosts(prev => prev.map(p => p.id === postId ? { ...p, status: newStatus } : p));
    try {
      const update = { status: newStatus, updatedAt: serverTimestamp() };
      if (newStatus === 'klar' && current.status === 'granskning') {
        update.approvedBy = userName;
        update.approvedAt = serverTimestamp();
        update.reviewComment = '';
      }
      await updateDoc(doc(db, 'linkedinPosts', postId), update);
    } catch (e) {
      console.error('Kunde inte uppdatera status:', e);
    }
  }, [posts, isReviewer, userName]);

  const toggleColumn = useCallback((statusId) => {
    setHiddenColumns(prev => {
      const next = prev.includes(statusId)
        ? prev.filter(c => c !== statusId)
        : [...prev, statusId];
      if (currentUser) updateDoc(doc(db, 'users', currentUser.uid), { linkedinHiddenCols: next }).catch(() => {});
      return next;
    });
  }, [currentUser]);

  const handleGridUpdate = useCallback(async (postId, fields) => {
    setPosts(prev => prev.map(p => p.id === postId ? { ...p, ...fields } : p));
    try {
      await updateDoc(doc(db, 'linkedinPosts', postId), { ...fields, updatedAt: serverTimestamp() });
    } catch (e) {
      console.error('Grid update failed:', e);
    }
  }, []);

  const activePost = activeId ? posts.find(p => p.id === activeId) : null;

  const totalActive = posts.filter(p => p.status !== 'publicerad').length;
  const awaitingReview = posts.filter(p => p.status === 'granskning').length;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Box sx={{
        bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', gap: 2,
        position: 'sticky', top: 0, zIndex: 100,
      }}>
        <LagbasLogo />
        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
        <IconButton color="inherit" size="small" onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>
        <LinkedInIcon sx={{ color: '#0a66c2', fontSize: '1.4rem' }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">LinkedIn Verkstad</Typography>
          <Typography variant="caption" sx={{ color: '#aaa' }}>
            {totalActive} aktiva inlägg i flödet
          </Typography>
        </Box>

        {/* Filter */}
        <FormControl size="small" sx={{ minWidth: 130, '& .MuiInputLabel-root': { color: '#aaa' }, '& .MuiOutlinedInput-root': { color: 'white', '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' } } }}>
          <InputLabel>Filtrera</InputLabel>
          <Select value={filterType} label="Filtrera" onChange={e => setFilterType(e.target.value)}>
            <MenuItem value=""><em>Alla</em></MenuItem>
            {Object.entries(POST_TYPES).map(([k, v]) => (
              <MenuItem key={k} value={k}>{v.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Kampanjer */}
        <Tooltip title="Innehållskampanjer">
          <IconButton size="small" onClick={() => navigate('/marketing/linkedin/kampanjer')}
            sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { color: 'white' } }}>
            <CampaignIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Kolumner */}
        {view === 'board' && (
          <Tooltip title="Visa/dölj kolumner">
            <IconButton size="small" onClick={e => setColAnchor(e.currentTarget)}
              sx={{ color: hiddenColumns.length > 0 ? '#FF6B00' : 'rgba(255,255,255,0.6)', '&:hover': { color: 'white' } }}>
              <ViewColumnIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Popover
          open={Boolean(colAnchor)} anchorEl={colAnchor} onClose={() => setColAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { p: 1.5, borderRadius: 2, minWidth: 170 } }}
        >
          <Typography variant="caption" color="text.secondary" fontWeight={700}
            sx={{ textTransform: 'uppercase', letterSpacing: 0.6, px: 0.5, display: 'block', mb: 0.5 }}>
            Visa kolumner
          </Typography>
          {STATUSES.map(s => (
            <FormControlLabel key={s.id}
              control={
                <Checkbox size="small" checked={!hiddenColumns.includes(s.id)}
                  onChange={() => toggleColumn(s.id)}
                  sx={{ '&.Mui-checked': { color: s.color } }}
                />
              }
              label={<Typography variant="body2" sx={{ color: s.color, fontWeight: 600 }}>{s.label}</Typography>}
              sx={{ display: 'flex', ml: 0, mr: 0 }}
            />
          ))}
        </Popover>

        {/* View toggle */}
        <Box sx={{ display: 'flex', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 1, overflow: 'hidden' }}>
          <Tooltip title="Planeringstavla">
            <IconButton size="small" onClick={() => setView('board')}
              sx={{ borderRadius: 0, bgcolor: view === 'board' ? 'rgba(255,255,255,0.15)' : 'transparent', color: 'white', px: 1.5 }}>
              <ViewKanbanIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Gridvy / redigera">
            <IconButton size="small" onClick={() => setView('grid')}
              sx={{ borderRadius: 0, bgcolor: view === 'grid' ? 'rgba(255,255,255,0.15)' : 'transparent', color: 'white', px: 1.5 }}>
              <TableRowsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Kalender">
            <IconButton size="small" onClick={() => setView('calendar')}
              sx={{ borderRadius: 0, bgcolor: view === 'calendar' ? 'rgba(255,255,255,0.15)' : 'transparent', color: 'white', px: 1.5 }}>
              <CalendarMonthIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Granskningsbadge – syns bara för granskare */}
        {isReviewer && awaitingReview > 0 && (
          <Tooltip title={`${awaitingReview} inlägg väntar på granskning`}>
            <Button
              variant="outlined"
              startIcon={
                <Badge badgeContent={awaitingReview} color="error">
                  <RateReviewIcon />
                </Badge>
              }
              onClick={() => { setView('board'); setFilterType(''); }}
              sx={{
                color: '#C62828', borderColor: '#C62828',
                '&:hover': { bgcolor: 'rgba(198,40,40,0.08)', borderColor: '#C62828' },
                fontWeight: 700, whiteSpace: 'nowrap', animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.7 },
                },
              }}
            >
              Väntar granskning
            </Button>
          </Tooltip>
        )}

        <Tooltip title="Importera förslag från Word/PDF">
          <IconButton size="small" onClick={() => setImportOpen(true)}
            sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { color: 'white' } }}>
            <UploadFileIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/marketing/linkedin/new')}
          sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' }, fontWeight: 700, whiteSpace: 'nowrap' }}
        >
          Nytt inlägg
        </Button>
      </Box>

      <LinkedInImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        existingPosts={posts}
        onImport={async (items) => {
          await Promise.all(items.map(item => {
            const themeFields = item.themeId
              ? { themeId: item.themeId, themeName: item.themeName, themeColor: item.themeColor, campaignId: item.campaignId, themeLabel: '' }
              : { themeLabel: item.themeLabel || item.tema, themeId: '', themeName: '', themeColor: '', campaignId: '' };
            if (item.existingId) {
              return updateDoc(doc(db, 'linkedinPosts', item.existingId), {
                ...themeFields,
                description: item.innehall,
                updatedAt:   serverTimestamp(),
              });
            }
            return addDoc(collection(db, 'linkedinPosts'), {
              title:        item.rubrik,
              ...themeFields,
              description:  item.innehall,
              type:         item.type,
              status:       'idé',
              content:      '',
              imageUrls:    [],
              likes:        [],
              comments:     [],
              productId:    item.productId || '',
              productName:  item.productName || '',
              createdBy:    currentUser?.uid || '',
              createdAt:    serverTimestamp(),
              updatedAt:    serverTimestamp(),
            });
          }));
        }}
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : view === 'calendar' ? (
        <Container maxWidth="xl" sx={{ py: 3 }}>
          <CalendarView posts={filteredPosts} navigate={navigate} />
        </Container>
      ) : view === 'grid' ? (
        <Container maxWidth="xl" sx={{ py: 3 }}>
          <GridView posts={filteredPosts} onUpdate={handleGridUpdate} navigate={navigate} />
        </Container>
      ) : (
        /* ── Kanban board ── */
        <Box sx={{ overflowX: 'auto', pb: 3 }}>
          <Box sx={{ px: 2, pt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              {/* Type filter chips */}
              {Object.entries(POST_TYPES).map(([k, v]) => (
                <Chip
                  key={k}
                  label={`${v.label} (${posts.filter(p => p.type === k).length})`}
                  size="small"
                  onClick={() => setFilterType(filterType === k ? '' : k)}
                  sx={{
                    bgcolor: filterType === k ? v.bg : 'transparent',
                    color: v.color,
                    border: `1px solid ${v.color}`,
                    fontWeight: filterType === k ? 700 : 400,
                    cursor: 'pointer',
                    fontSize: '0.72rem',
                  }}
                />
              ))}
              {/* Sökfält */}
              <TextField
                size="small"
                placeholder="Sök inlägg…"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                sx={{
                  width: 200,
                  '& .MuiInputBase-root': { fontSize: '0.78rem', height: 28 },
                  '& .MuiInputBase-input': { py: 0.5 },
                }}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ fontSize: '1rem', color: 'text.secondary', mr: 0.5 }} />,
                  endAdornment: searchQuery ? (
                    <IconButton size="small" onClick={() => setSearchQuery('')} sx={{ p: 0.25 }}>
                      <ClearIcon sx={{ fontSize: '0.9rem' }} />
                    </IconButton>
                  ) : null,
                }}
              />
              {/* Sort */}
              <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>Sortera:</Typography>
                {[
                  { key: 'none',    label: 'Standard' },
                  { key: 'title',   label: 'Namn' },
                  { key: 'type',    label: 'Produkt' },
                  { key: 'theme',   label: 'Tema' },
                  { key: 'updated', label: 'Uppdaterad' },
                ].map(opt => (
                  <Chip key={opt.key} label={opt.label} size="small"
                    onClick={() => setBoardSort(opt.key)}
                    sx={{
                      cursor: 'pointer',
                      fontSize: '0.72rem',
                      bgcolor: boardSort === opt.key ? '#1a1a1a' : 'transparent',
                      color: boardSort === opt.key ? 'white' : 'text.secondary',
                      border: `1px solid ${boardSort === opt.key ? '#1a1a1a' : '#ccc'}`,
                      fontWeight: boardSort === opt.key ? 700 : 400,
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Box>

          <DndContext
            sensors={sensors}
            collisionDetection={rectIntersection}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
          >
            <Box sx={{ display: 'flex', gap: 1.5, px: 2, width: '100%' }}>
              {visibleStatuses.map(status => {
                const colPosts = postsForStatus(status.id);
                const isColumnOver = overId === status.id;
                return (
                  <DroppableColumn key={status.id} id={status.id} isOver={isColumnOver}>
                    {/* Column header */}
                    <Box sx={{
                      px: 1.5, py: 1.25, display: 'flex', alignItems: 'center', gap: 0.75,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}>
                      <Box sx={{ color: status.color }}>{status.icon}</Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', flex: 1 }}>
                        {status.label}
                      </Typography>
                      <Chip
                        label={colPosts.length}
                        size="small"
                        sx={{ height: 18, fontSize: '0.65rem', bgcolor: 'rgba(0,0,0,0.08)', fontWeight: 700 }}
                      />
                      {status.id === 'idé' && (
                        <Tooltip title="Nytt inlägg">
                          <IconButton
                            size="small"
                            onClick={() => navigate('/marketing/linkedin/new')}
                            sx={{ p: 0.3, ml: -0.5 }}
                          >
                            <AddIcon sx={{ fontSize: '0.9rem' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Dölj kolumn">
                        <IconButton
                          size="small"
                          onClick={() => toggleColumn(status.id)}
                          sx={{ p: 0.3, color: 'text.disabled', '&:hover': { color: 'text.secondary' } }}
                        >
                          <VisibilityOffIcon sx={{ fontSize: '0.85rem' }} />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    {/* Cards */}
                    <Box sx={{ p: 1, flex: 1 }}>
                      {colPosts.length === 0 && (
                        <Box sx={{
                          textAlign: 'center', py: 3, color: '#bbb',
                          fontSize: '0.75rem', userSelect: 'none',
                        }}>
                          Dra ett inlägg hit
                        </Box>
                      )}
                      {colPosts.map(post => (
                        <DraggableCard
                          key={post.id}
                          post={post}
                          navigate={navigate}
                          onDelete={handleDelete}
                        />
                      ))}
                    </Box>
                  </DroppableColumn>
                );
              })}
            </Box>

            {/* Drag overlay */}
            <DragOverlay>
              {activePost && (
                <Paper elevation={8} sx={{
                  p: 1.5, borderRadius: 2, minWidth: 220,
                  borderLeft: `4px solid ${(POST_TYPES[activePost.type] || POST_TYPES.tikab).color}`,
                  bgcolor: '#fff',
                }}>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>
                    {activePost.title || '(Namnlöst inlägg)'}
                  </Typography>
                </Paper>
              )}
            </DragOverlay>
          </DndContext>
        </Box>
      )}
    </Box>
  );
}
